/*
 * = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = =
 *
 * File: nrdropdown.component.ts
 * Project: nr-common
 * Created Date: We May 2024
 * Author: Fabio Capelletti
 *
 * Last Modified: Wed Aug 14 2024
 * Modified By: Fabio Capelletti
 *
 * Copyright (c) 2024 Naar Tour Operator spa
 *
 * HISTORY:
 * Date    	By	Comments
 * --------	---	---------------------------------------------------------
 * 20240814	FC	Aggiunta la possibilità di specificare la posizione del tooltip tramite la property tooltipPlacement (right<default> | left | top | bottom)
 */

import {
	Component,
	EventEmitter,
	Input,
	OnChanges,
	OnInit,
	Output,
	SimpleChanges,
	ViewChild,
	ViewEncapsulation,
} from '@angular/core';
import {
	AbstractControl,
	ControlValueAccessor,
	FormControl,
	FormGroup,
	NG_VALIDATORS,
	NG_VALUE_ACCESSOR,
	ValidationErrors,
	Validator,
	Validators,
} from '@angular/forms';
import {
	DropDownListComponent,
	ComboBoxComponent,
} from '@progress/kendo-angular-dropdowns';

import { NrDropdownSource } from './nrdropdownsource.class';
import { BackendService } from '../backend/backend.service';
import { BaseDataService } from '../basedata/basedata.service';
import { NrDropdownItem } from './nrdropdownitem.class';

@Component({
    selector: 'nr-dropdown',
    templateUrl: './nrdropdown.component.html',
    styleUrls: ['./nrdropdown.component.scss'],
    encapsulation: ViewEncapsulation.None,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: NrDropdownComponent,
        },
        {
            provide: NG_VALIDATORS,
            multi: true,
            useExisting: NrDropdownComponent,
        },
    ],
    standalone: false
})
export class NrDropdownComponent
	implements OnInit, OnChanges, ControlValueAccessor, Validator
{
	// OM 22.05.2024: dinamically change the table
	// @Input() table: string;
	private _table: string;
	public get table(): string {
		return this._table;
	}
	@Input() public set table(v: string) {
		if (v != null && v != this._table) {
			this._table = v;
			this._itemsSource = new NrDropdownSource(
				this._table,
				this.backend,
				this.baseData
			);
		}
	}

	@Input() placeholder: string;
	@Input() showIdOnOptions = false;
	@Input() showIdOnItem = false;
	@Input() qualifierTranslatePrefix: string = null;
	@Input() popOverData = false;
	@Input() formControlName: string;
	@Input() formControl: AbstractControl;

	private _required: boolean;
	public get required(): boolean {
		return this._required;
	}
	@Input()
	public set required(v: boolean) {
		this._required = v;

		if (this._required) {
			this.dropDownForm.get('comboboxControl').setValidators([Validators.required]);
		} else {
			this.dropDownForm.get('comboboxControl').clearValidators();
		}
	}

	@Input() valueField = 'id';
	@Input() textField = 'label';
	@Input() size = 'small';

	@Input() public foreingKeyName: string = null;
	@Input() public foreingKeyValue: string = null;
	@Input() public nameFormat: string = null;

	@Output() selected: EventEmitter<NrDropdownItem> = new EventEmitter<NrDropdownItem>();

	private _suggestedItems: NrDropdownItem[];
	public get suggestedItems(): NrDropdownItem[] {
		return this._suggestedItems;
	}
	@Input() public set suggestedItems(v: NrDropdownItem[]) {
		if (this._suggestedItems !== v) {
			this._itemsSource = null;
		}
		this._suggestedItems = v;
	}

	@Input() public tooltipPlacement: string = 'right';

	// OM 25.03.2025
	public dropDownForm = new FormGroup({
		comboboxControl: new FormControl({value:null, disabled: false}),
	});

	private _itemsSource: NrDropdownSource;
	public get itemsSource(): NrDropdownSource {
		if (!this._itemsSource && this.table) {
			this._itemsSource = new NrDropdownSource(
				this.table,
				this.backend,
				this.baseData,
				this.suggestedItems
			);
		}
		return this._itemsSource;
	}

	public value: any = null;
	public disabled = false;

	private earlyWriteValueObj: any;

	private _label: string;
	public get label(): string {
		return this._label;
	}

	constructor(
		private backend: BackendService,
		private baseData: BaseDataService
	) {}

	validate(control: AbstractControl): ValidationErrors {
		if (this.required) {
			const value = control.value;
			if (value == null) {
				return {
					required: true,
				};
			}
		}
		return null;
	}

	onValidationChange: () => void;
	registerOnValidatorChange?(fn: () => void): void {
		this.onValidationChange = fn;
	}

	ngOnInit(): void {
		if (this.earlyWriteValueObj) {
			this.itemsSource.getItem(this.earlyWriteValueObj).subscribe(
				(item) => {
					this.value = item; //? item[this.valueField] : null;
					this._label = item ? item[this.textField] : null;
				},
				(err) => {
					console.error(err);
				}
			);
		}
	}

	onChange(_: any) {}
	registerOnChange(fn: any): void {
		this.onChange = fn;
	}

	private touched = false;
	onTouched() {}
	registerOnTouched(fn: any): void {
		this.onTouched = fn;
	}

	setDisabledState(isDisabled: boolean): void {
		this.disabled = isDisabled;
	}

	writeValue(obj: any): void {
		if (this.itemsSource) {
			this.itemsSource.getItem(obj).subscribe(
				(item) => {
					this.value = item; //? item[this.valueField] : null;
					this._label = item ? item[this.textField] : null;
					/* if (item)
					this.selected.emit(item); */
				},
				(err) => {
					console.error(err);
				}
			);
		} else {
			this.earlyWriteValueObj = obj;
		}
	}

	setValue(obj: any) {
		this.value = obj;
		this.onChange(obj ? obj[this.valueField] : null);
	}

	onSelectionChanged(item: any) {
		if (item) {
			this._label = item.label;
		} else {
			this._label = null;
		}
		this.selected.emit(item || null);
	}

	public filterText = '';
	onFilterChanged(value: string) {
		this.filterText = value;
		this._itemsSource.query$.next(value);
	}

	ngOnChanges(changes: SimpleChanges): void {
		this.itemsSource.foreingKeyName = this.foreingKeyName;
		this.itemsSource.foreingKeyValue = this.foreingKeyValue;
		this.itemsSource.nameFormat = this.nameFormat;
	}

	markAsTouched() {
		if (!this.touched) {
			this.onTouched();
			this.touched = true;
		}
	}

	@ViewChild(DropDownListComponent) list: DropDownListComponent;
	@ViewChild(ComboBoxComponent) combo: ComboBoxComponent;
	public showList = true;
	onListFocus() {
		this.showList = false;
		setTimeout(() => {
			this.combo.focus();
			this.combo.toggle(true);
		}, 100);
	}

	onComboBlur() {
		this.showList = true;
	}
}
